import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/icon/Icon";
import { Dialog } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatKst } from "@/lib/datetime";
import { formatWon } from "@/lib/quote";
import type {
  BoardDetailDTO,
  BoardItemDTO,
  BoardListResponse,
  BoardUnlockResponse,
  InquiryProgress,
  QuoteSnapshotDTO,
} from "@/types/inquiry";
import dchainConnectMark from "@/assets/brand/dchain-connect-mark.svg";

const PAGE_SIZE = 20;

/** formatKst 는 옵션을 안 주면 시:분:초까지 붙어요 — 게시판엔 날짜만 있으면 돼요. */
const DATE_ONLY: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };

/** 처리 현황 라벨은 웹이 원본이에요 — api 는 이 값으로 계산하지 않아요. */
const PROGRESS_LABEL: Record<InquiryProgress, string> = {
  received: "접수",
  in_progress: "처리중",
  done: "완료",
};

const PROGRESS_STYLE: Record<InquiryProgress, string> = {
  received: "bg-background-alternative text-label-neutral",
  in_progress: "bg-primary text-static-white",
  done: "bg-background-alternative text-primary",
};

function ProgressBadge({ progress }: { progress: InquiryProgress }) {
  return (
    <span
      className={`shrink-0 rounded-full px-3 py-1 wds-caption-1 font-semibold ${PROGRESS_STYLE[progress]}`}
    >
      {PROGRESS_LABEL[progress]}
    </span>
  );
}

/** 공개 문의 전문과 비밀번호로 연 전문을 한 모양으로 그려요 — 이름은 둘 다 마스킹된 값이에요. */
interface DetailView {
  created_at: string;
  progress: InquiryProgress;
  displayName: string;
  type_label: string | null;
  message: string;
  quote: QuoteSnapshotDTO | null;
}

function UnlockDialog({
  item,
  onClose,
}: {
  item: BoardItemDTO | null;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<DetailView | null>(null);

  // 다른 글을 열 때 이전 입력·결과가 남아 있으면 안 돼요.
  useEffect(() => {
    setPassword("");
    setError(null);
    setDetail(null);
    // 공개 문의는 비밀번호를 묻지 않고 바로 받아와요. 공개 여부 판정은 서버가 해요(403).
    if (!item?.is_public) return;
    let alive = true;
    api
      .get<BoardDetailDTO>(`/inquiry-board/${item.id}`)
      .then((d) => {
        if (alive) setDetail({ ...d, displayName: d.masked_name });
      })
      .catch(() => {
        if (alive) setError("내용을 불러오지 못했어요.");
      });
    return () => {
      alive = false;
    };
  }, [item]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<BoardUnlockResponse>(`/inquiry-board/${item.id}/unlock`, { password });
      setDetail({ ...res, displayName: res.name, type_label: item.type_label });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "열람하지 못했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={item !== null} onClose={onClose} title={item?.is_public ? "문의 내용" : "내 문의 열람"}>
      {detail ? (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 wds-body-2">
            <dt className="text-label-assistive">접수일</dt>
            <dd className="text-label-normal">{formatKst(detail.created_at, DATE_ONLY)}</dd>
            <dt className="text-label-assistive">작성자</dt>
            <dd className="text-label-normal">{detail.displayName}</dd>
            {detail.type_label && (
              <>
                <dt className="text-label-assistive">프로젝트 유형</dt>
                <dd className="text-label-normal">{detail.type_label}</dd>
              </>
            )}
            <dt className="text-label-assistive">처리 현황</dt>
            <dd className="text-label-normal">{PROGRESS_LABEL[detail.progress]}</dd>
          </dl>
          <p className="whitespace-pre-wrap rounded-md bg-background-alternative p-4 wds-body-2-reading text-label-normal">
            {detail.message}
          </p>
          {detail.quote && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 wds-body-2">
              <dt className="text-label-assistive">페이지 수</dt>
              <dd className="tabular-nums text-label-normal">{detail.quote.pages}</dd>
              <dt className="text-label-assistive">예상 금액</dt>
              <dd className="tabular-nums text-label-normal">{formatWon(detail.quote.discounted)}</dd>
            </dl>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <p className="wds-body-2 text-label-neutral">
            문의하실 때 정하신 조회 비밀번호를 입력해 주세요.
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            aria-label="조회 비밀번호"
            className="h-11 rounded-md border border-line-normal bg-background-normal px-4 wds-body-2 text-label-normal"
          />
          {error && <output className="wds-caption-1 text-status-negative">{error}</output>}
          <button
            type="submit"
            disabled={busy || password.length === 0}
            className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 wds-body-2 font-semibold text-static-white disabled:opacity-40"
          >
            {busy ? "확인 중" : "열람하기"}
          </button>
        </form>
      )}
    </Dialog>
  );
}

export function InquiryBoardPage() {
  const [data, setData] = useState<BoardListResponse | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<BoardItemDTO | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.get<BoardListResponse>(`/inquiry-board?page=${page}&size=${PAGE_SIZE}`));
    } catch {
      setError("문의 현황을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.");
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="min-h-screen bg-background-normal">
      <header className="sticky top-0 z-50 border-line-alternative border-b bg-background-elevated">
        <nav className="mx-auto flex h-16 max-w-layout items-center justify-between px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="디체인커넥트 홈">
            <img src={dchainConnectMark} alt="" width="34" height="28" className="h-7 w-auto" />
            <span className="wds-headline-1 text-label-strong">디체인커넥트</span>
          </Link>
          <Link to="/inquiry" className="wds-label-1 font-semibold text-primary">
            문의하기
          </Link>
        </nav>
      </header>

      <main className="mx-auto flex max-w-layout flex-col gap-8 px-6 py-16">
        <div className="flex flex-col gap-3">
          <h1 className="wds-title-1 text-label-strong">문의 현황</h1>
          <p className="wds-body-1-reading text-label-neutral">
            접수된 문의와 처리 현황이에요. 문의 내용은 기본적으로 비공개라 다른 분께 보이지 않아요.
            본인 문의는 문의하실 때 정하신 조회 비밀번호로 확인하실 수 있어요.
          </p>
        </div>

        {error && <output className="wds-body-2 text-status-negative">{error}</output>}

        <ul className="flex flex-col divide-y divide-line-alternative border-line-alternative border-y">
          {data?.items.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-4 py-4">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="flex items-center gap-1.5">
                  {!item.is_public && (
                    <Icon name="lockFill" size={14} className="shrink-0 text-label-assistive" />
                  )}
                  <span className="truncate wds-body-2 font-medium text-label-normal">
                    {item.is_public ? item.title : "비공개 문의입니다"}
                  </span>
                </span>
                <span className="wds-caption-1 text-label-assistive">
                  {formatKst(item.created_at, DATE_ONLY)} · {item.masked_name}
                  {item.type_label && ` · ${item.type_label}`}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <ProgressBadge progress={item.progress} />
                {(item.is_public || item.unlockable) && (
                  <button
                    type="button"
                    onClick={() => setSelected(item)}
                    className="wds-caption-1 font-semibold text-primary underline underline-offset-4"
                  >
                    {item.is_public ? "내용 보기" : "내 문의 열기"}
                  </button>
                )}
              </div>
            </li>
          ))}
          {data?.items.length === 0 && (
            <li className="py-12 text-center wds-body-2 text-label-assistive">아직 접수된 문의가 없어요.</li>
          )}
        </ul>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="wds-label-1 text-label-neutral disabled:opacity-40"
            >
              이전
            </button>
            <span className="wds-caption-1 tabular-nums text-label-assistive">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="wds-label-1 text-label-neutral disabled:opacity-40"
            >
              다음
            </button>
          </div>
        )}
      </main>

      <UnlockDialog item={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default InquiryBoardPage;
